import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Card, Deck, Space, Battle, CardType, Faction, Rarity } from '../types'

interface CardFilters {
  search: string
  type: CardType | 'ALL'
  faction: Faction | 'ALL'
  rarity: Rarity | 'ALL'
  costMin: number
  costMax: number
}

interface DeckEditorState {
  currentDeck: Deck | null
  selectedCardId: string | null
  isDirty: boolean
}

interface EvolutionState {
  isRunning: boolean
  currentGeneration: number
  bestFitness: number
  avgFitness: number
  populationSize: number
  targetGenerations: number
  bestDeck: Deck | null
}

interface BattleState {
  currentBattle: Battle | null
  battleLog: Array<{
    turn: number
    player: string
    action: string
    timestamp: string
  }>
  isRunning: boolean
  currentTurn: number
  player1Health: number
  player2Health: number
}

interface UserState {
  isAuthenticated: boolean
  user: {
    id: string
    email: string
    username: string
    avatar?: string
  } | null
  preferences: {
    theme: 'dark' | 'light'
    language: string
    animationsEnabled: boolean
  }
}

interface AppState {
  cards: Card[]
  decks: Deck[]
  spaces: Space[]
  cardFilters: CardFilters
  deckEditor: DeckEditorState
  evolution: EvolutionState
  battle: BattleState
  user: UserState
  isLoading: boolean
  error: string | null
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      cards: [],
      decks: [],
      spaces: [],
      cardFilters: {
        search: '',
        type: 'ALL' as CardType | 'ALL',
        faction: 'ALL' as Faction | 'ALL',
        rarity: 'ALL' as Rarity | 'ALL',
        costMin: 0,
        costMax: 10,
      },
      deckEditor: {
        currentDeck: null,
        selectedCardId: null,
        isDirty: false,
      },
      evolution: {
        isRunning: false,
        currentGeneration: 0,
        bestFitness: 0,
        avgFitness: 0,
        populationSize: 50,
        targetGenerations: 100,
        bestDeck: null,
      },
      battle: {
        currentBattle: null,
        battleLog: [],
        isRunning: false,
        currentTurn: 0,
        player1Health: 30,
        player2Health: 30,
      },
      user: {
        isAuthenticated: false,
        user: null,
        preferences: {
          theme: 'dark',
          language: 'es',
          animationsEnabled: true,
        },
      },
      isLoading: false,
      error: null,

      actions: {
        setCards: (cards: Card[]) => set({ cards }),
        addCard: (card: Card) => set((state) => ({ cards: [...state.cards, card] })),
        updateCard: (id: string, data: Partial<Card>) =>
          set((state) => ({
            cards: state.cards.map((c) => (c.id === id ? { ...c, ...data } : c)),
          })),
        deleteCard: (id: string) =>
          set((state) => ({ cards: state.cards.filter((c) => c.id !== id) })),

        setDecks: (decks: Deck[]) => set({ decks }),
        addDeck: (deck: Deck) => set((state) => ({ decks: [...state.decks, deck] })),
        updateDeck: (id: string, data: Partial<Deck>) =>
          set((state) => ({
            decks: state.decks.map((d) => (d.id === id ? { ...d, ...data } : d)),
          })),
        deleteDeck: (id: string) =>
          set((state) => ({ decks: state.decks.filter((d) => d.id !== id) })),

        setSpaces: (spaces: Space[]) => set({ spaces }),
        addSpace: (space: Space) => set((state) => ({ spaces: [...state.spaces, space] })),
        updateSpace: (id: string, data: Partial<Space>) =>
          set((state) => ({
            spaces: state.spaces.map((s) => (s.id === id ? { ...s, ...data } : s)),
          })),
        deleteSpace: (id: string) =>
          set((state) => ({ spaces: state.spaces.filter((s) => s.id !== id) })),

        setCardFilters: (filters: Partial<CardFilters>) =>
          set((state) => ({ cardFilters: { ...state.cardFilters, ...filters } })),
        resetCardFilters: () =>
          set({
            cardFilters: {
              search: '',
              type: 'ALL',
              faction: 'ALL',
              rarity: 'ALL',
              costMin: 0,
              costMax: 10,
            },
          }),

        setCurrentDeck: (deck: Deck | null) =>
          set((state) => ({ deckEditor: { ...state.deckEditor, currentDeck: deck, isDirty: false } })),
        setSelectedCardId: (id: string | null) =>
          set((state) => ({ deckEditor: { ...state.deckEditor, selectedCardId: id } })),
        setDeckDirty: (isDirty: boolean) =>
          set((state) => ({ deckEditor: { ...state.deckEditor, isDirty } })),
        addCardToDeck: (cardId: string, quantity: number = 1) => {
          const state = get()
          const deck = state.deckEditor.currentDeck
          if (!deck) return

          const existingIndex = deck.cards.findIndex((c) => c.cardId === cardId)
          let newCards
          if (existingIndex >= 0) {
            newCards = [...deck.cards]
            newCards[existingIndex] = {
              ...newCards[existingIndex],
              quantity: newCards[existingIndex].quantity + quantity,
            }
          } else {
            const card = state.cards.find((c) => c.id === cardId)
            if (!card) return
            newCards = [...deck.cards, { cardId, card, quantity }]
          }

          set({
            deckEditor: {
              ...state.deckEditor,
              currentDeck: { ...deck, cards: newCards },
              isDirty: true,
            },
          })
        },
        removeCardFromDeck: (cardId: string) => {
          const state = get()
          const deck = state.deckEditor.currentDeck
          if (!deck) return

          const newCards = deck.cards
            .map((c) => (c.cardId === cardId ? { ...c, quantity: c.quantity - 1 } : c))
            .filter((c) => c.quantity > 0)

          set({
            deckEditor: {
              ...state.deckEditor,
              currentDeck: { ...deck, cards: newCards },
              isDirty: true,
            },
          })
        },
        clearDeckEditor: () =>
          set({
            deckEditor: {
              currentDeck: null,
              selectedCardId: null,
              isDirty: false,
            },
          }),

        startEvolution: (config?: { populationSize?: number; generations?: number }) =>
          set((state) => ({
            evolution: {
              ...state.evolution,
              isRunning: true,
              currentGeneration: 0,
              bestFitness: 0,
              avgFitness: 0,
              populationSize: config?.populationSize || 50,
              targetGenerations: config?.generations || 100,
            },
          })),
        pauseEvolution: () =>
          set((state) => ({ evolution: { ...state.evolution, isRunning: false } })),
        resumeEvolution: () =>
          set((state) => ({ evolution: { ...state.evolution, isRunning: true } })),
        stopEvolution: () =>
          set((state) => ({ evolution: { ...state.evolution, isRunning: false } })),
        setEvolutionGeneration: (gen: number) =>
          set((state) => ({ evolution: { ...state.evolution, currentGeneration: gen } })),
        setEvolutionFitness: (best: number, avg: number) =>
          set((state) => ({ evolution: { ...state.evolution, bestFitness: best, avgFitness: avg } })),
        setBestDeck: (deck: Deck) =>
          set((state) => ({ evolution: { ...state.evolution, bestDeck: deck } })),
        resetEvolution: () =>
          set({
            evolution: {
              isRunning: false,
              currentGeneration: 0,
              bestFitness: 0,
              avgFitness: 0,
              populationSize: 50,
              targetGenerations: 100,
              bestDeck: null,
            },
          }),

        startBattle: (deck1Id: string, deck2Id?: string) =>
          set({
            battle: {
              currentBattle: {
                id: crypto.randomUUID(),
                status: 'IN_PROGRESS',
                deck1Id,
                deck2Id,
                turns: 0,
                createdAt: new Date().toISOString(),
              } as Battle,
              battleLog: [],
              isRunning: true,
              currentTurn: 0,
              player1Health: 30,
              player2Health: 30,
            },
          }),
        pauseBattle: () =>
          set((state) => ({ battle: { ...state.battle, isRunning: false } })),
        resumeBattle: () =>
          set((state) => ({ battle: { ...state.battle, isRunning: true } })),
        endBattle: (winnerId: string) =>
          set((state) => ({
            battle: {
              ...state.battle,
              currentBattle: state.battle.currentBattle
                ? { ...state.battle.currentBattle, status: 'COMPLETED', winnerId }
                : null,
              isRunning: false,
            },
          })),
        addBattleLog: (entry: { turn: number; player: string; action: string }) =>
          set((state) => ({
            battle: {
              ...state.battle,
              battleLog: [
                ...state.battle.battleLog,
                { ...entry, timestamp: new Date().toLocaleTimeString() },
              ],
            },
          })),
        setPlayerHealth: (player: 1 | 2, health: number) =>
          set((state) => ({
            battle: {
              ...state.battle,
              [`player${player}Health`]: health,
            },
          })),
        nextTurn: () =>
          set((state) => ({
            battle: {
              ...state.battle,
              currentTurn: state.battle.currentTurn + 1,
            },
          })),
        resetBattle: () =>
          set({
            battle: {
              currentBattle: null,
              battleLog: [],
              isRunning: false,
              currentTurn: 0,
              player1Health: 30,
              player2Health: 30,
            },
          }),

        login: (user: { id: string; email: string; username: string; avatar?: string }) =>
          set({
            user: {
              isAuthenticated: true,
              user,
              preferences: get().user.preferences,
            },
          }),
        logout: () =>
          set({
            user: {
              isAuthenticated: false,
              user: null,
              preferences: get().user.preferences,
            },
          }),
        updatePreferences: (prefs: Partial<UserState['preferences']>) =>
          set((state) => ({
            user: {
              ...state.user,
              preferences: { ...state.user.preferences, ...prefs },
            },
          })),

        setLoading: (isLoading: boolean) => set({ isLoading }),
        setError: (error: string | null) => set({ error }),
        clearError: () => set({ error: null }),
      },

      filteredCards: () => {
        const { cards, cardFilters } = get()
        return cards.filter((card) => {
          const matchesSearch =
            cardFilters.search === '' ||
            card.name.toLowerCase().includes(cardFilters.search.toLowerCase()) ||
            card.cardId.toLowerCase().includes(cardFilters.search.toLowerCase())
          const matchesType = cardFilters.type === 'ALL' || card.type === cardFilters.type
          const matchesFaction = cardFilters.faction === 'ALL' || card.faction === cardFilters.faction
          const matchesRarity = cardFilters.rarity === 'ALL' || card.rarity === cardFilters.rarity
          const matchesCost =
            card.cost >= cardFilters.costMin && card.cost <= cardFilters.costMax
          return matchesSearch && matchesType && matchesFaction && matchesRarity && matchesCost
        })
      },

      deckStats: () => {
        const { deckEditor } = get()
        const deck = deckEditor.currentDeck
        if (!deck) return null

        const totalCards = deck.cards.reduce((sum, c) => sum + c.quantity, 0)
        const byType = deck.cards.reduce(
          (acc, c) => {
            acc[c.card.type] = (acc[c.card.type] || 0) + c.quantity
            return acc
          },
          {} as Record<string, number>
        )
        const byFaction = deck.cards.reduce(
          (acc, c) => {
            acc[c.card.faction] = (acc[c.card.faction] || 0) + c.quantity
            return acc
          },
          {} as Record<string, number>
        )
        const costCurve = deck.cards.reduce(
          (acc, c) => {
            acc[c.card.cost] = (acc[c.card.cost] || 0) + c.quantity
            return acc
          },
          {} as Record<number, number>
        )

        return {
          totalCards,
          byType,
          byFaction,
          costCurve,
          avgCost:
            totalCards > 0
              ? deck.cards.reduce((sum, c) => sum + c.card.cost * c.quantity, 0) / totalCards
              : 0,
        }
      },
    }),
    {
      name: 'tcg-gen-storage-v2',
      partialize: (state) => ({
        user: state.user,
        cardFilters: state.cardFilters,
      }),
    }
  )
)

export const { actions, filteredCards, deckStats } = useAppStore.getState()
